import React, { useEffect, useMemo } from 'react'
import FacebookAdSetBreakdown from '../LineItemsBreakdown/FacebookAdSetBreakdown'
import { useDispatch, useSelector } from 'react-redux'

import PageSectionHeader from '../../../../../features/components/PageSectionHeader'
import TikTokAdGroupBreakdown from '../LineItemsBreakdown/TiktokAdGroupBreakdown'
import GoogleAdGroupBreakdown from '../LineItemsBreakdown/GoogleAdGroupBreakdown'
import DV360AdGroupBreakdown from '../LineItemsBreakdown/DV360LineItemBreakdown'
import FacebookHighestPerformingAds from '../HighestPerformingAds/FacebookHighestPerformingAds'
import GoogleHighestPerformingAds from '../HighestPerformingAds/GoogleHighestPerformingAds'
import TikTokHighestPerformingAds from '../HighestPerformingAds/TikTokHighestPerformingAds'
import DV360HighestPerformingAds from '../HighestPerformingAds/DV360HighestPerformingAds'

import { useGetProvidersFromCampaigns } from '../hooks'
import { groupCampaignsByProvider } from '../../RightCampaigns/helpers'

import { clearCombinedAds, clearCombinedLineItems, getCombinedAds } from '../../../../../modules/actions/combinedData'
import {
  combinedCampaignsSelector,
  combinedLineItemsIsLoadingSelector,
  combinedLineItemsSelector
} from '../../../../../modules/selectors/combinedData'
import { rightSelector } from '../../../../../modules/selectors/rights'

import {
  DV_360_PLATFORM,
  DV_360_PROVIDER_PLATFORM,
  FACEBOOK_PLATFORM,
  FACEBOOK_PROVIDER_PLATFORM,
  GOOGLE_PLATFORM,
  GOOGLE_PROVIDER_PLATFORM,
  TIKTOK_PLATFORM,
  TIKTOK_PROVIDER_PLATFORM
} from '../../../../../constants/selectLists/platformList'

const ReportsSections = () => {
  const dispatch = useDispatch()

  const combinedLineItems = useSelector(combinedLineItemsSelector)
  const combinedLineItemsIsLoading = useSelector(combinedLineItemsIsLoadingSelector)
  const { campaigns: rightCampaignsToLoad } = useSelector(rightSelector)
  const { campaigns_date_range: campaignsDateRange } = useSelector(combinedCampaignsSelector)
  const { date_from, date_to } = campaignsDateRange || {}

  const providers = useGetProvidersFromCampaigns(rightCampaignsToLoad)

  const campaignsGroupedByProvider = useMemo(
    () => groupCampaignsByProvider(rightCampaignsToLoad),
    [rightCampaignsToLoad]
  )

  const facebookCombinedLineItems = useMemo(
    () => combinedLineItems.filter(({ platform }) => platform === FACEBOOK_PLATFORM),
    [combinedLineItems]
  )

  const googleCombinedLineItems = useMemo(
    () => combinedLineItems.filter(({ platform }) => platform === GOOGLE_PLATFORM),
    [combinedLineItems]
  )

  const tiktokCombinedLineItems = useMemo(
    () => combinedLineItems.filter(({ platform }) => platform === TIKTOK_PLATFORM),
    [combinedLineItems]
  )

  const dv360CombinedLineItems = useMemo(
    () => combinedLineItems.filter(({ platform }) => platform === DV_360_PLATFORM),
    [combinedLineItems]
  )

  const showFacebookSections = useMemo(
    () =>
      providers.includes(FACEBOOK_PROVIDER_PLATFORM) &&
      (!!facebookCombinedLineItems.length || combinedLineItemsIsLoading),
    [providers, facebookCombinedLineItems, combinedLineItemsIsLoading]
  )

  const showGoogleSections = useMemo(
    () =>
      providers.includes(GOOGLE_PROVIDER_PLATFORM) && (!!googleCombinedLineItems.length || combinedLineItemsIsLoading),
    [providers, googleCombinedLineItems, combinedLineItemsIsLoading]
  )

  const showTiktokSections = useMemo(
    () =>
      providers.includes(TIKTOK_PROVIDER_PLATFORM) && (!!tiktokCombinedLineItems.length || combinedLineItemsIsLoading),
    [providers, tiktokCombinedLineItems, combinedLineItemsIsLoading]
  )

  const showDv360Sections = useMemo(
    () =>
      providers.includes(DV_360_PROVIDER_PLATFORM) && (!!dv360CombinedLineItems.length || combinedLineItemsIsLoading),
    [providers, dv360CombinedLineItems, combinedLineItemsIsLoading]
  )

  useEffect(() => {
    dispatch(
      getCombinedAds({
        insights: [
          'impressions',
          'cpm',
          'actions_like',
          'actions_post',
          'actions_post_reaction',
          'reach',
          'frequency',
          'clicks',
          'ctr',
          'conversions',
          'engagement',
          'results',
          'video_view_rate',
          'video_views'
        ],
        date_from,
        date_to,
        providers: campaignsGroupedByProvider
      })
    )
  }, [dispatch, date_from, date_to, campaignsGroupedByProvider])

  useEffect(() => {
    return () => {
      dispatch(clearCombinedAds())
      dispatch(clearCombinedLineItems())
    }
  }, [dispatch])

  return (
    <>
      {showFacebookSections && (
        <>
          <section id="facebook_breakdowns">
            <PageSectionHeader title="Facebook" />
            <FacebookAdSetBreakdown campaigns={facebookCombinedLineItems} />
          </section>
          <FacebookHighestPerformingAds />
        </>
      )}
      {showGoogleSections && (
        <>
          <section id="google_breakdowns">
            <PageSectionHeader title="Google Ads" />
            <GoogleAdGroupBreakdown campaigns={googleCombinedLineItems} />
          </section>
          <GoogleHighestPerformingAds />
        </>
      )}
      {showTiktokSections && (
        <>
          <section id="tiktok_breakdowns">
            <PageSectionHeader title="Tiktok" />
            <TikTokAdGroupBreakdown campaigns={tiktokCombinedLineItems} />
          </section>
          <TikTokHighestPerformingAds />
        </>
      )}
      {showDv360Sections && (
        <>
          <section id="dv360_breakdowns">
            <PageSectionHeader title="Display or Video" />
            <DV360AdGroupBreakdown campaigns={dv360CombinedLineItems} />
          </section>
          <DV360HighestPerformingAds />
        </>
      )}
    </>
  )
}

export default ReportsSections
